import * as Sentry from '@sentry/browser';

if (window.__sentry?.dsn) {
    Sentry.init({
        dsn: window.__sentry.dsn,
        release: window.__sentry.release,
        environment: window.__sentry.environment,
        integrations: [
            Sentry.browserTracingIntegration(),
        ],
        tracesSampleRate: 0.05,
    });
}

import './bootstrap';
import 'chart.js';
import Chart from 'chart.js/auto';
window.Chart = Chart;
import '../../vendor/masmerise/livewire-toaster/resources/js';
import Clipboard from 'clipboard';
import QRCode from 'qrcode';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

/**
 * SKY-450: Leaflet is bundled here instead of being fetched from unpkg.com on
 * every public club-page view (SKYTTELOGGEN-14D, "L is not defined" whenever the
 * CDN did not load). Vite hashes or inlines the marker images, so Leaflet's
 * CSS-based icon-path detection cannot find them — point the default icon at
 * the bundled assets and drop the path-detecting override.
 */
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: markerIcon2x,
    iconUrl: markerIcon,
    shadowUrl: markerShadow,
});

// Vite — glob registers assets for bundling; return value intentionally unused.
// eager + ?url emits each file as a manifest asset so Vite::asset() can resolve
// them (Vite 8/Rolldown no longer emits bare lazy globs into the manifest).
import.meta.glob([
    '../pdf/polisen/**',
    '../front/images/**',
    '../images/**',
], { eager: true, query: '?url', import: 'default' });

/**
 * SKY-92: Alpine components.
 *
 * The CSP does not allow 'unsafe-eval', so Livewire serves Alpine's CSP build
 * (config/livewire.php → csp_safe). Its expression parser cannot handle arrow
 * functions, multiple statements, `if`, `const`, getters, browser globals or
 * x-html inside Blade attributes. Any logic beyond a single simple expression
 * lives here as a named component and is referenced from the view, e.g.
 * `x-data="arenaMap(59.3, 18.0)"`.
 *
 * Alpine itself is bundled with Livewire; register on `window.Alpine` during
 * `alpine:init` rather than importing the npm package (a second, never-started
 * Alpine instance). tests/Feature/Security/AlpineCspExpressionsTest.php fails
 * on view expressions the CSP build cannot run.
 */
document.addEventListener('alpine:init', () => {
    const Alpine = window.Alpine;

    /**
     * Arena map with known GPS coordinates.
     * Uses Leaflet to render a static map tile at the given lat/lon directly,
     * without any geocoding request.
     */
    Alpine.data('arenaMap', (lat, lon) => ({
        _map: null,
        _observer: null,
        init() {
            // Guard against Livewire DOM morphing re-triggering init
            // on an element Leaflet already owns
            if (this.$el._leaflet_id) {
                return;
            }
            this._map = L.map(this.$el, {
                scrollWheelZoom: false,
                dragging: false,
                zoomControl: false,
                attributionControl: true
            }).setView([lat, lon], 14);
            L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
                attribution: '&copy; OpenStreetMap'
            }).addTo(this._map);
            L.marker([lat, lon]).addTo(this._map);
            // Flux tab panels use display:none for inactive tabs,
            // so Leaflet initialises on a 0×0 container.
            // ResizeObserver fires when the panel becomes visible
            // and invalidateSize() redraws tiles correctly.
            this._observer = new ResizeObserver(() => {
                if (this._map && this.$el.offsetWidth > 0) {
                    this._map.invalidateSize();
                }
            });
            this._observer.observe(this.$el);
        },
        destroy() {
            if (this._observer) {
                this._observer.disconnect();
                this._observer = null;
            }
            if (this._map) {
                this._map.remove();
                this._map = null;
            }
        }
    }));

    /**
     * SKY-319: client-side animated BankID QR code.
     *
     * Eliminates the wire:poll.1s="pollQr" round-trip that kept racing
     * Auth::login()'s CSRF rotation in SKYTTELOGGEN-138. Each tick is signed
     * locally with HMAC-SHA256 per the BankID spec:
     *   bankid.{qrStartToken}.{time}.{hex(HMAC-SHA256(time, qrStartSecret))}
     *
     * The seed material is read from $wire on every tick (not snapshotted), so
     * the Livewire morph that flips qrLogin true also propagates the freshly-issued
     * values into the running interval. The SVG is written to the element directly
     * because the CSP build prohibits x-html.
     */
    Alpine.data('bankidQr', () => ({
        _interval: null,
        init() {
            this.tick();
            this._interval = setInterval(() => this.tick(), 1000);
        },
        destroy() {
            if (this._interval) {
                clearInterval(this._interval);
                this._interval = null;
            }
        },
        async tick() {
            const token = this.$wire.qrStartToken;
            const secret = this.$wire.qrStartSecret;
            const startTimestamp = this.$wire.qrStartTimestamp;
            if (!token || !secret || !startTimestamp) {
                this.$el.innerHTML = '';
                return;
            }
            const time = Math.max(0, Math.floor(Date.now() / 1000 - startTimestamp));
            try {
                const enc = new TextEncoder();
                const key = await crypto.subtle.importKey(
                    'raw',
                    enc.encode(secret),
                    { name: 'HMAC', hash: 'SHA-256' },
                    false,
                    ['sign'],
                );
                const sig = await crypto.subtle.sign('HMAC', key, enc.encode(String(time)));
                const hex = Array.from(new Uint8Array(sig))
                    .map((b) => b.toString(16).padStart(2, '0'))
                    .join('');
                const data = `bankid.${token}.${time}.${hex}`;
                this.$el.innerHTML = await QRCode.toString(data, { type: 'svg', margin: 1, width: 250 });
            } catch (err) {
                // Next tick will retry; surface to Sentry if available.
                if (window.Sentry?.captureException) {
                    window.Sentry.captureException(err);
                }
            }
        },
    }));

    /**
     * Livewire Chart.js chart from icehouse-ventures/laravel-chartjs. Moved here from the package's
     * @script block — see resources/views/vendor/laravelchartjs/chart-template-livewire.blade.php.
     */
    Alpine.data('chart', Alpine.skipDuringClone((config, dataset, options) => {
        let chart;

        return {
            init() {
                chart = this.initChart();
            },
            destroy() {
                chart.destroy();
            },
            initChart() {
                const { element, type } = config;
                const el = this.$wire.$el.querySelector('canvas#' + element);
                const { labels, datasets } = dataset;

                return new Chart(el, {
                    type: type,
                    data: {
                        labels: labels,
                        datasets: datasets,
                    },
                    options: options,
                });
            },
        };
    }));

    /**
     * Public club page: keeps document.title in sync with the active tab.
     * Used as `x-effect="syncDocumentTitle()"` so it re-runs when $wire.activeTab changes.
     */
    Alpine.data('teamPageTitle', (teamName, tabLabels, appName) => ({
        syncDocumentTitle() {
            const suffix = tabLabels[this.$wire.activeTab];
            const pageTitle = suffix ? teamName + ' - ' + suffix : teamName;
            document.title = pageTitle + ' — ' + appName;
        },
    }));

    /**
     * Team page settings: tracks unsaved changes and blocks the links to other
     * settings pages while there are any.
     */
    Alpine.data('teamPageSettings', (initialTab) => ({
        tab: initialTab,
        dirty: false,
        blockNavigationWhileDirty(event) {
            if (this.dirty) {
                event.preventDefault();
                event.stopImmediatePropagation();
            }
        },
    }));

    /**
     * Local preview of an image picked in a file input, shown before the
     * Livewire upload has finished.
     */
    Alpine.data('imageUploadPreview', () => ({
        preview: null,
        showPreview(event) {
            const file = event.target.files[0];
            if (!file) {
                return;
            }
            const reader = new FileReader();
            reader.onload = (e) => {
                this.preview = e.target.result;
            };
            reader.readAsDataURL(file);
        },
    }));

    /**
     * Toast inside the livewire-toaster hub (resources/views/vendor/toaster/hub.blade.php).
     * Showing it on the next tick lets the x-show enter transition run.
     */
    Alpine.data('toasterToast', (toast) => ({
        init() {
            this.$nextTick(() => toast.show(this.$el));
        },
    }));

    /**
     * Flashes a message for two seconds when the surrounding Livewire component
     * dispatches the given event (<x-action-message on="saved">).
     */
    Alpine.data('actionMessage', (event) => ({
        shown: false,
        timeout: null,
        _stopListening: null,
        init() {
            this._stopListening = this.$wire.on(event, () => {
                clearTimeout(this.timeout);
                this.shown = true;
                this.timeout = setTimeout(() => {
                    this.shown = false;
                }, 2000);
            });
        },
        destroy() {
            clearTimeout(this.timeout);
            this._stopListening?.();
        },
    }));

    /**
     * Jetstream banner (<x-banner>): session flash on page load, updated by the
     * `banner-message` browser event.
     */
    Alpine.data('banner', (initial) => ({
        show: true,
        style: initial.style,
        message: initial.message,
        showMessage(event) {
            this.style = event.detail.style;
            this.message = event.detail.message;
            this.show = true;
        },
    }));

    /**
     * Two-factor challenge: switches between the authentication code and a
     * recovery code, focusing the visible input.
     */
    Alpine.data('twoFactorChallenge', () => ({
        recovery: false,
        useRecoveryCode() {
            this.recovery = true;
            this.$nextTick(() => this.$refs.recovery_code.focus());
        },
        useAuthenticationCode() {
            this.recovery = false;
            this.$nextTick(() => this.$refs.code.focus());
        },
    }));

    /**
     * Certificate form: opens the generated certificate preview in a new tab when
     * the component dispatches `preview-redirect`.
     */
    Alpine.data('certificatePreviewRedirect', () => ({
        _stopListening: null,
        init() {
            this._stopListening = this.$wire.on('preview-redirect', (options) => {
                window.open(options.url, '_blank')?.focus();
            });
        },
        destroy() {
            this._stopListening?.();
        },
    }));
});

let clipboard = new Clipboard('.copy-to-clipboard');

clipboard.on('success', function(e) {
    Toaster.success('Kopierat till urklipp:\n\n ' + e.text)
    e.clearSelection();
});

clipboard.on('error', function(e) {
    Toaster.error('Kunde inte kopiera texten....')

});
